import React from "react";
import { Link } from "react-router-dom";
import {
  Layers3,
  Globe,
  ShoppingCart,
  Zap,
  Cpu,
  Palette,
  ShieldCheck,
  ArrowRight,
  Sparkles,
  Check,
  Clock,
  PhoneCall,
  Laptop,
  Smartphone,
} from "lucide-react";
import { useModals } from "../contexts/ModalContext";

export default function WebdesignMegaMenu({ onClose, isMobile = false }) {
  const { openQuote } = useModals();

  // Categorical Webdesign Packages
  const webdesignPackages = [
    {
      id: "landingpage",
      name: "Landing-Page & Onepager",
      desc: "Konversionsstarker Single-Page Auftritt für Produkte, Kampagnen, KMU & Startups.",
      badge: "Lead-Magnet",
      badgeClass: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
      price: "Ab CHF 590.-",
      icon: Laptop,
      iconBox: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
      to: "/webdesign?package=landingpage",
    },
    {
      id: "corporate",
      name: "KMU & Corporate Website",
      desc: "Mehrseitiger Unternehmensauftritt mit individuellem Design, CMS, Blog & Google SEO.",
      badge: "Bestseller KMU",
      badgeClass: "bg-amber-500/15 text-amber-300 border-amber-500/30",
      price: "Ab CHF 1'490.-",
      icon: Globe,
      iconBox: "bg-amber-500/15 text-amber-400 border-amber-500/30",
      isFeatured: true,
      to: "/webdesign?package=corporate",
    },
    {
      id: "ecommerce",
      name: "E-Commerce & Online-Shop",
      desc: "Verkaufen Sie online mit WooCommerce oder Shopify. Schweizer Zahlungsarten (TWINT, Stripe, QR).",
      badge: "TWINT Ready",
      badgeClass: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
      price: "Ab CHF 2'290.-",
      icon: ShoppingCart,
      iconBox: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      to: "/webdesign?package=ecommerce",
    },
    {
      id: "redesign",
      name: "WordPress & Redesign",
      desc: "Frischer Look, blitzschnelle Ladezeiten, Responsive-Optimierung für bestehende Websites.",
      badge: "PageSpeed 95+",
      badgeClass: "bg-purple-500/15 text-purple-300 border-purple-500/30",
      price: "Ab CHF 990.-",
      icon: Zap,
      iconBox: "bg-purple-500/15 text-purple-400 border-purple-500/30",
      to: "/webdesign?package=redesign",
    },
    {
      id: "webapp",
      name: "Individuelle Web-Applikation",
      desc: "Kundenspezifische Portale, Buchungssysteme, Datenbanken, API-Schnittstellen und SaaS.",
      badge: "Full-Stack Custom",
      badgeClass: "bg-blue-500/15 text-blue-300 border-blue-500/30",
      price: "Ab CHF 2'990.-",
      icon: Cpu,
      iconBox: "bg-blue-500/15 text-blue-400 border-blue-500/30",
      to: "/webdesign?package=webapp",
    },
    {
      id: "branding",
      name: "UI/UX Design & Branding",
      desc: "Logo-Design, Corporate Identity, Design-Systeme, Figma-Prototypen & Styleguides.",
      badge: "Design Studio",
      badgeClass: "bg-pink-500/15 text-pink-300 border-pink-500/30",
      price: "Ab CHF 490.-",
      icon: Palette,
      iconBox: "bg-pink-500/15 text-pink-400 border-pink-500/30",
      to: "/webdesign?package=branding",
    },
    {
      id: "wartung",
      name: "Website-Wartung & SLA",
      desc: "Wöchentliche Sicherheits-Updates, tägliche Backups, 24/7 Monitoring & Schweizer Notfall-Support.",
      badge: "SLA Flatrate",
      badgeClass: "bg-sky-500/15 text-sky-300 border-sky-500/30",
      price: "Ab CHF 79.- / Mt.",
      icon: ShieldCheck,
      iconBox: "bg-sky-500/15 text-sky-400 border-sky-500/30",
      to: "/webdesign?package=wartung",
    },
  ];

  // ============================================================================
  // MOBILE / TABLET VIEW (Inside Drawer)
  // ============================================================================
  if (isMobile) {
    return (
      <div className="space-y-1.5 pt-1">
        {webdesignPackages.map((item) => {
          const IconComp = item.icon;
          return (
            <Link
              key={item.id}
              to={item.to}
              onClick={onClose}
              className="flex items-center justify-between p-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/5 transition"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border ${item.iconBox}`}>
                  <IconComp size={14} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold text-white truncate">{item.name}</div>
                  <div className="text-[10px] text-slate-400 line-clamp-1">{item.desc}</div>
                </div>
              </div>
              <span className="text-[11px] font-mono font-bold text-[#D4AF37] shrink-0 ml-2">
                {item.price}
              </span>
            </Link>
          );
        })}

        {/* Webview App Special Feature Card for Mobile */}
        <Link
          to="/webdesign#webview-app"
          onClick={onClose}
          className="flex items-center justify-between p-2.5 rounded-xl bg-gradient-to-r from-[#E63946]/15 via-[#FF7A00]/10 to-transparent border border-[#E63946]/30 transition hover:border-[#E63946]"
        >
          <div className="flex items-center gap-2">
            <Smartphone size={14} className="text-[#E63946]" />
            <span className="text-xs font-bold text-white">Website als App (Webview)</span>
            <span className="text-[9px] bg-[#E63946] text-white px-1.5 py-0.2 rounded font-mono font-bold">NEU</span>
          </div>
          <span className="text-[11px] font-mono font-bold text-[#D4AF37]">ab CHF 299.-</span>
        </Link>

        <Link
          to="/webdesign"
          onClick={onClose}
          className="flex items-center justify-center gap-1.5 p-2.5 rounded-xl text-xs font-bold text-slate-300 hover:text-white hover:bg-white/5 transition mt-1"
        >
          <span>Alle Webdesign-Pakete ansehen</span>
          <ArrowRight size={13} />
        </Link>
      </div>
    );
  }

  // ============================================================================
  // DESKTOP LUXURY MEGA-MENU
  // ============================================================================
  const leftCol = webdesignPackages.slice(0, 4);
  const rightCol = webdesignPackages.slice(4);

  return (
    <div
      role="menu"
      aria-label="Webdesign Dropdown"
      className="w-[880px] max-w-[95vw] mx-auto rounded-[28px] bg-[#07090E] text-white shadow-[0_30px_90px_rgba(0,0,0,0.95)] border border-white/15 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
    >
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-[#FF7A00]/20 via-[#E63946]/15 to-transparent px-6 py-3.5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-2 w-2 rounded-full bg-[#FF7A00] animate-pulse" />
          <span className="text-xs font-black uppercase tracking-wider text-white">
            🇨🇭 Schweizer Webdesign & Digitale Exzellenz
          </span>
          <span className="text-slate-500">•</span>
          <span className="text-xs text-slate-300 font-medium">
            Mobile-First, PageSpeed 95+ & Conversion-Optimiert
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/webdesign#webview-app"
            onClick={onClose}
            className="text-[11px] font-black text-white bg-gradient-to-r from-[#E63946] to-[#FF7A00] px-3 py-1 rounded-full shadow-sm hover:opacity-90 transition flex items-center gap-1.5"
          >
            <Smartphone size={12} />
            <span>Neu: App (Webview) ab CHF 299.-</span>
          </Link>
          <Link
            to="/webdesign"
            onClick={onClose}
            className="text-xs font-extrabold text-[#FF9E40] hover:text-white flex items-center gap-1 transition"
          >
            <span>Preise & Pakete vergleichen</span>
            <ArrowRight size={13} />
          </Link>
        </div>
      </div>

      {/* Main 2-Column Grid */}
      <div className="p-6 grid grid-cols-12 gap-6">
        {/* Left Column: Core Webdesign Packages (7 Cols) */}
        <div className="col-span-7 space-y-2 border-r border-white/10 pr-6">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">
            WEBSITE & E-COMMERCE PAKETE
          </p>
          {leftCol.map((item) => {
            const IconComp = item.icon;
            return (
              <Link
                key={item.id}
                to={item.to}
                onClick={onClose}
                className={`group flex items-start gap-3.5 p-3 rounded-2xl transition duration-200 border ${
                  item.isFeatured
                    ? "bg-gradient-to-r from-[#FF7A00]/10 via-[#E63946]/5 to-transparent border-[#FF7A00]/30 hover:border-[#FF7A00]"
                    : "hover:bg-white/[0.04] border-transparent hover:border-white/10"
                }`}
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${item.iconBox} transition-transform duration-200 group-hover:scale-110 shadow-sm mt-0.5`}
                >
                  <IconComp size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-extrabold text-white group-hover:text-[#FF9E40] transition truncate">
                      {item.name}
                    </span>
                    <span className="text-xs font-mono font-bold text-[#FF9E40] shrink-0">
                      {item.price}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-1 mt-0.5 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Right Column: Web Apps, Branding & Wartung (5 Cols) */}
        <div className="col-span-5 flex flex-col justify-between space-y-4">
          <div className="space-y-2">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">
              INDIVIDUAL & DESIGN SERVICES
            </p>
            {rightCol.map((item) => {
              const IconComp = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={onClose}
                  className="group flex items-start gap-3 p-2.5 rounded-xl hover:bg-white/[0.04] border border-transparent hover:border-white/10 transition"
                >
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${item.iconBox} mt-0.5`}
                  >
                    <IconComp size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-extrabold text-white group-hover:text-[#FF9E40] transition truncate">
                        {item.name}
                      </span>
                      <span className="text-[11px] font-mono font-bold text-slate-300 shrink-0">
                        {item.price}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 line-clamp-1 mt-0.5">
                      {item.desc}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Direct Quote Card */}
          <div className="rounded-2xl bg-gradient-to-br from-[#121828] to-[#0A0D16] p-4 border border-white/15 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-wider text-[#FF9E40] flex items-center gap-1">
                <Sparkles size={11} />
                <span>KOSTENLOSE BERATUNG</span>
              </span>
              <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                100% UNVERBINDLICH
              </span>
            </div>
            <p className="text-xs text-slate-300 leading-snug">
              Haben Sie ein konkretes Projekt? Wir erstellen Ihnen innert 24h eine detaillierte Schweizer Festpreis-Offerte.
            </p>
            <button
              type="button"
              onClick={() => {
                if (onClose) onClose();
                if (openQuote) openQuote();
              }}
              className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-[#FF7A00] to-[#E63946] hover:from-[#e56d00] hover:to-[#c5303d] text-white font-black text-xs flex items-center justify-center gap-1.5 shadow-md shadow-orange-600/20 transition cursor-pointer"
            >
              <PhoneCall size={13} />
              <span>Projekt anfragen & Offerte anfordern</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Feature Strip */}
      <div className="bg-black/50 px-6 py-2.5 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-400 font-medium">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <Check size={13} className="text-emerald-400" />
            100% Responsive für Mobile & Tablet
          </span>
          <span className="flex items-center gap-1">
            <Check size={13} className="text-emerald-400" />
            Schweizer DSG Konformität
          </span>
          <span className="flex items-center gap-1">
            <Check size={13} className="text-emerald-400" />
            Nahtlose Hosting-Integration
          </span>
        </div>
        <span className="text-slate-500 font-mono">Zürich • Basel • Bern</span>
      </div>
    </div>
  );
}
